<script lang="ts">
	// A2: whether Bosla grows and whether restaurants use it (UC-21). It names
	// no restaurant. Each figure carries the line that says what it does not
	// mean, because each can be misread: junk sign-ups count as restaurants,
	// one large restaurant can move the orders per day alone, and a till that
	// is switched off keeps the app build it last reported (UC-21.R7).

	import Band from '../components/Band.svelte';
	import Card from '../components/Card.svelte';
	import Loading from '../components/Loading.svelte';
	import Meter from '../components/Meter.svelte';
	import Row from '../components/Row.svelte';
	import StatCard from '../components/StatCard.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { copy } from './copy';
	import { dayWithWeekday } from './format';
	import { sessionEnded } from './session.svelte';

	type Totals = components['schemas']['AdminOverviewOutputBody'];

	let totals = $state<Totals | null>(null);
	let failed = $state(false);

	async function load() {
		failed = false;
		try {
			const { data, response } = await api.GET('/api/admin/overview');
			if (response.status === 401) {
				sessionEnded();
				return;
			}
			if (!data) {
				failed = true;
				return;
			}
			totals = data;
		} catch {
			failed = true;
		}
	}

	$effect(() => {
		void load();
	});

	// Today first, as the page reads down from now.
	const days = $derived((totals?.ordersPerDay ?? []).slice().reverse());
	const peak = $derived(Math.max(0, ...days.map((d) => d.count)));

	const columns = [
		{ label: copy.dayColumn, w: '112px' },
		{ label: '', w: 'minmax(0,1fr)' },
		{ label: copy.ordersColumn, w: '72px', align: 'end' as const },
	];

	// Lowest build first, as the server sends them; build 0 is no build known.
	const builds = $derived(totals?.tillsPerBuild ?? []);
	const buildColumns = [
		{ label: copy.buildColumn, w: 'minmax(0,1fr)' },
		{ label: copy.tillsCountColumn, w: '72px', align: 'end' as const },
	];
</script>

<div class="overview" data-testid="admin-overview">
	{#if failed}
		<Band
			tone="flag"
			action={copy.retry}
			onAction={() => void load()}
			testId="admin-overview-failed"
		>
			{copy.overviewFailed}
		</Band>
	{:else if !totals}
		<div class="wait">
			<Loading label={copy.overviewLoading} caption={copy.overviewLoading} size="lg" />
		</div>
	{:else}
		{#if totals.restaurants === 0}
			<Card pad testId="admin-no-restaurants">
				<p class="empty">{copy.noRestaurants}</p>
			</Card>
		{:else}
			<div class="figures">
				<div class="cards">
					<StatCard
						label={copy.restaurants}
						value={String(totals.restaurants)}
						mono
						valueTestId="admin-restaurants"
					>
						{copy.restaurantsNote}
					</StatCard>
					<StatCard
						label={copy.fresh}
						value={String(totals.newLast7Days)}
						mono
						valueTestId="admin-new"
					>
						{copy.freshNote}
					</StatCard>
					<StatCard
						label={copy.selling}
						value={copy.sellingValue(totals.sellingLast7Days, totals.restaurants)}
						mono
						valueTestId="admin-selling"
					>
						{copy.sellingNote}
					</StatCard>
				</div>
				<p class="note">{copy.countsNote}</p>
			</div>
		{/if}
		<div class="days">
			<TablePanel
				{columns}
				title={copy.ordersPerDay}
				note={copy.ordersPerDayNote}
				rowMin="0"
				testId="admin-orders-per-day"
			>
				{#each days as d, i (d.date)}
					<Row style="padding-block: var(--space-3)" testId="admin-day-{d.date}">
						<span class="day">
							<span class="label">{dayWithWeekday(d.date)}</span>
							{#if i === 0}<span class="today">{copy.todaySoFar}</span>{/if}
						</span>
						<span class="bar">
							<Meter
								value={peak ? Math.round((d.count / peak) * 100) : 0}
								dim={peak > 0 && d.count !== peak}
								label={dayWithWeekday(d.date)}
							/>
						</span>
						<span class="count">{d.count.toLocaleString('en')}</span>
					</Row>
				{/each}
			</TablePanel>
			<p class="note">{copy.loadNote}</p>
		</div>
		<div class="builds">
			<TablePanel
				columns={buildColumns}
				title={copy.tillsPerBuild}
				note={copy.tillsPerBuildNote}
				rowMin="0"
				testId="admin-tills-per-build"
			>
				{#each builds as b (b.appBuild)}
					<Row style="padding-block: var(--space-3)" testId="admin-build-{b.appBuild}">
						<span class="build" class:unknown={b.appBuild === 0}>
							{b.appBuild === 0 ? copy.buildNotReported : copy.appBuild(b.appBuild)}
						</span>
						<span class="count">{b.tills.toLocaleString('en')}</span>
					</Row>
				{:else}
					<Row style="padding-block: var(--space-3)" testId="admin-no-paired-tills">
						<span class="none">{copy.noPairedTills}</span>
						<span></span>
					</Row>
				{/each}
			</TablePanel>
			<p class="note">{copy.buildsNote}</p>
		</div>
	{/if}
</div>

<style>
	.overview {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.figures {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.cards {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
		gap: var(--grid-gap);
	}

	.note {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.days {
		max-inline-size: 720px;
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.builds {
		max-inline-size: 720px;
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.day {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		font-size: var(--text-sm);
	}

	.label {
		font-weight: 600;
		white-space: nowrap;
	}

	.build {
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.build.unknown,
	.none {
		font-size: var(--text-sm);
		font-weight: 400;
		color: var(--text-body);
	}

	.today {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.bar {
		min-inline-size: 0;
		display: flex;
		align-items: center;
	}

	.count {
		min-inline-size: 0;
		font-family: var(--font-mono);
		font-weight: 700;
		text-align: end;
	}
</style>
